import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, User, Brain, Settings, Zap, DollarSign, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { toast } from "sonner";

export const ChatHeader = ({
  // Navigation
  matchId,
  match,
  showSessionList,
  onBack,
  // Chat info
  chatTitle,
  chatSubtitle,
  sessionTokens,
  sessionCost,
  coachingTokens = 0,
  coachingCost = 0,
  // Session mode
  currentSessionId,
  currentSessionMode,
  // V2 system toggle
  v2SystemEnabled,
  setV2SystemEnabled,
  onV2SystemToggle,
  // Advice chat controls (non-match)
  adviceUseUserData,
  setAdviceUseUserData,
  selectedMatchForAdvice,
  setSelectedMatchForAdvice,
  allMatches,
  adviceResponseLength,
  setAdviceResponseLength,
  // Persona chat controls
  maxWords,
  setMaxWords,
  showCoachAnnotations,
  setShowCoachAnnotations,
  // V2 Tips toggle (coach_v2)
  showV2TipsInChat,
  setShowV2TipsInChat,
  // V1 Tips toggle (coach_overlay_agent)
  showV1TipsInChat,
  setShowV1TipsInChat,
  useV2,
  // Model selection (shared)
  selectedModel,
  setSelectedModel,
  availableModels,
  // Objectives panel
  onOpenObjectives,
}) => {
  const navigate = useNavigate();
  const [tokenDropdownOpen, setTokenDropdownOpen] = useState(false);
  
  // Calculate combined totals
  const totalTokens = (sessionTokens || 0) + (coachingTokens || 0);
  const totalCost = (sessionCost || 0) + (coachingCost || 0);
  
  const formatCost = (cost) => {
    if (!cost || cost < 0.0001) return "<$0.0001";
    return `$${cost.toFixed(4)}`;
  };

  return (
    <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-50">
      <div className="max-w-4xl mx-auto px-6 py-2 flex items-center gap-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={onBack}
          className="text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        {/* Avatar - Clickable to match profile */}
        <div 
          className={`w-10 h-10 rounded-full bg-divine-red/20 flex items-center justify-center overflow-hidden ${matchId ? 'cursor-pointer hover:ring-2 hover:ring-divine-gold/50 transition-all' : ''}`}
          onClick={() => matchId && navigate(`/relationships/${matchId}`)}
          data-testid="chat-header-avatar"
        >
          {matchId && match ? (
            (match.thumbnail_url || match.picture_url || match.picture) ? (
              <img src={match.thumbnail_url || match.picture_url || match.picture} alt={match.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-divine-gold font-cinzel">{match.name[0]}</span>
            )
          ) : (
            <div className="relative">
              <div className="absolute inset-0 bg-divine-gold/50 blur-sm rounded-full" />
              <Brain className="w-5 h-5 text-divine-gold relative" />
            </div>
          )}
        </div>
        
        {/* Title */}
        <div className="flex-1 min-w-0">
          <h1 className="font-cinzel text-lg text-white truncate">{chatTitle}</h1>
          <p className="text-white/40 text-xs truncate">{chatSubtitle}</p>
        </div>

        {/* Controls for Advice Chat (non-match) - directly in header */}
        {!matchId && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Match Context Dropdown */}
            <select
              value={selectedMatchForAdvice || ""}
              onChange={(e) => setSelectedMatchForAdvice(e.target.value || null)}
              className="bg-divine-black/80 border border-white/20 text-white text-xs rounded px-2 py-1.5 focus:ring-divine-gold/50 focus:border-divine-gold/50 cursor-pointer max-w-[140px]"
              data-testid="advice-match-selector"
              title="Select a match to get personalized advice"
            >
              <option value="">General Advice</option>
              {allMatches.map((m) => (
                <option key={m.match_id} value={m.match_id}>
                  {m.name}
                </option>
              ))}
            </select>

            {/* User Data Toggle */}
            <div 
              className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer transition-colors ${
                adviceUseUserData 
                  ? "bg-divine-gold/20 border border-divine-gold/40" 
                  : "bg-white/5 border border-white/10 hover:bg-white/10"
              }`}
              onClick={() => setAdviceUseUserData(!adviceUseUserData)}
              title="Include your profile data for personalized advice"
              data-testid="advice-user-data-toggle"
            >
              <User className={`w-3.5 h-3.5 ${adviceUseUserData ? "text-divine-gold" : "text-white/40"}`} />
              <span className={`text-xs ${adviceUseUserData ? "text-divine-gold" : "text-white/40"}`}>
                My Data
              </span>
            </div>

            {/* Settings Popover (Response Length & Model only) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10"
                  data-testid="advice-settings-btn"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-64 bg-divine-black border border-white/20 p-4"
                align="end"
              >
                <div className="space-y-4">
                  <h4 className="font-medium text-white text-sm">Response Settings</h4>
                  
                  {/* V2 Coaching Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Brain className={`w-4 h-4 ${showCoachAnnotations ? "text-cyan-400" : "text-white/40"}`} />
                      <span className="text-white/80 text-sm">V2 Coaching</span>
                    </div>
                    <Switch
                      checked={showCoachAnnotations}
                      onCheckedChange={setShowCoachAnnotations}
                      data-testid="v2-coaching-toggle"
                    />
                  </div>
                  
                  {/* Response Length */}
                  <div className="space-y-2">
                    <label className="text-white/60 text-xs">Response Length</label>
                    <select
                      value={adviceResponseLength}
                      onChange={(e) => setAdviceResponseLength(parseInt(e.target.value))}
                      className="w-full bg-divine-black/80 border border-white/20 text-white text-sm rounded px-3 py-2 focus:ring-divine-gold/50 focus:border-divine-gold/50 cursor-pointer"
                      data-testid="advice-length-selector"
                    >
                      <option value={75}>Short (~75 words)</option>
                      <option value={150}>Medium (~150 words)</option>
                      <option value={300}>Long (~300 words)</option>
                      <option value={500}>Detailed (~500 words)</option>
                    </select>
                  </div>

                  {/* AI Model Selection */}
                  <div className="space-y-2">
                    <label className="text-white/60 text-xs">AI Model</label>
                    <select
                      value={selectedModel ? `${selectedModel.provider}/${selectedModel.name}` : "null/null"}
                      onChange={(e) => {
                        if (e.target.value === "null/null") {
                          setSelectedModel(null);
                        } else {
                          const [provider, name] = e.target.value.split("/");
                          const model = availableModels.find(m => m.provider === provider && m.name === name);
                          if (model) setSelectedModel(model);
                        }
                      }}
                      className="w-full bg-divine-black/80 border border-white/20 text-white text-sm rounded px-3 py-2 focus:ring-divine-gold/50 focus:border-divine-gold/50 cursor-pointer"
                      data-testid="advice-model-selector"
                    >
                      {availableModels.map((model) => (
                        <option key={model.provider ? `${model.provider}/${model.name}` : "null/null"} value={model.provider ? `${model.provider}/${model.name}` : "null/null"}>
                          {model.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Integrated Controls for Persona Chat */}
        {matchId && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Session Mode Badge - Clickable to show mode and open objectives */}
            {currentSessionId && currentSessionMode !== "regular" && (
              <div 
                className={`px-2 py-1 rounded text-xs font-medium cursor-pointer transition-colors hover:opacity-80 ${
                  currentSessionMode === "escalation" 
                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" 
                    : "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                }`}
                onClick={() => {
                  const modeLabel = currentSessionMode === "escalation" ? "Escalation Mode" : "Practice Mode";
                  const modeDesc = currentSessionMode === "escalation" 
                    ? "Working toward relationship escalation goals" 
                    : "Practicing conversation techniques";
                  toast.info(`${currentSessionMode === "escalation" ? "🎮" : "🎯"} ${modeLabel}`, {
                    description: modeDesc
                  });
                  if (onOpenObjectives) {
                    onOpenObjectives();
                  }
                }}
                title="Click to view objectives"
                data-testid="session-mode-badge"
              >
                {currentSessionMode === "escalation" ? "🎮 Escalation" : "🎯 Practice"}
              </div>
            )}
            
            {/* Agent Eros Advice Toggle - Master on/off */}
            <div 
              className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer transition-colors ${
                showCoachAnnotations 
                  ? "bg-divine-gold/20 border border-divine-gold/40" 
                  : "bg-white/5 border border-white/10 hover:bg-white/10"
              }`}
              onClick={() => setShowCoachAnnotations(!showCoachAnnotations)}
              title="Toggle Agent Eros Advice"
              data-testid="eros-advice-toggle"
            >
              <span className="text-sm">🏹</span>
              <span className={`text-xs font-medium ${showCoachAnnotations ? "text-divine-gold" : "text-white/40"}`}>
                Eros
              </span>
            </div>
            
            {/* Settings Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10"
                  data-testid="chat-settings-btn"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-72 bg-divine-black border border-white/20 p-4"
                align="end"
              >
                <div className="space-y-4">
                  <h4 className="font-medium text-white text-sm">Chat Settings</h4>
                  
                  {/* Token Usage - Mobile Only */}
                  <div className="md:hidden space-y-2 pb-3 border-b border-white/10">
                    <div className="flex items-center gap-2 text-divine-gold">
                      <Zap className="w-4 h-4" />
                      <span className="text-sm font-medium">Token Usage</span>
                    </div>
                    <div className="space-y-1.5 pl-1">
                      <div className="flex items-center justify-between">
                        <span className="text-white/50 text-xs">Chat Tokens</span>
                        <span className="text-white font-medium text-sm">{(sessionTokens || 0).toLocaleString()}</span>
                      </div>
                      {coachingTokens > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-cyan-400/70 text-xs">Coaching Tokens</span>
                          <span className="text-cyan-400 font-medium text-sm">{coachingTokens.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-1 border-t border-white/10">
                        <span className="text-white/50 text-xs">Total</span>
                        <span className="text-white font-medium text-sm">{totalTokens.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white/50 text-xs">Est. Cost</span>
                        <span className="text-divine-gold font-medium text-sm">{formatCost(totalCost)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Agent Eros Advice Section */}
                  <div className="space-y-3 pb-3 border-b border-white/10">
                    <div className="flex items-center gap-2 text-divine-gold">
                      <span>🏹</span>
                      <span className="text-sm font-medium">Agent Eros Advice</span>
                    </div>
                    
                    {/* V2 System Toggle */}
                    <div className="space-y-1.5 pl-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${v2SystemEnabled ? "" : "opacity-40"}`}>🏹</span>
                          <label className="text-white text-xs">V2 System</label>
                        </div>
                        <Switch
                          id="v2-system-toggle"
                          data-testid="v2-system-toggle"
                          checked={v2SystemEnabled}
                          onCheckedChange={(checked) => {
                            setV2SystemEnabled(checked);
                            if (onV2SystemToggle) onV2SystemToggle(checked);
                          }}
                          className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/20 h-4 w-7"
                        />
                      </div>
                      <p className="text-white/40 text-[10px] pl-6">
                        Enable V2 Coaching{currentSessionMode === "escalation" ? " + Escalator" : ""}
                      </p>
                    </div>
                    
                    {/* V2 Tips Toggle */}
                    <div className="space-y-1.5 pl-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${showV2TipsInChat ? "" : "opacity-40"}`}>🏹</span>
                          <label className="text-white text-xs">V2 Tips</label>
                        </div>
                        <Switch
                          id="v2-tips-toggle"
                          data-testid="v2-tips-toggle"
                          checked={showV2TipsInChat}
                          onCheckedChange={setShowV2TipsInChat}
                          className="data-[state=checked]:bg-cyan-500 data-[state=unchecked]:bg-white/20 h-4 w-7"
                        />
                      </div>
                      <p className="text-white/40 text-[10px] pl-6">
                        Enable V2 Coaching Tips
                      </p>
                    </div>
                    
                    {/* V1 Tips Toggle */}
                    <div className="space-y-1.5 pl-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${showV1TipsInChat ? "" : "opacity-40"}`}>🏹</span>
                          <label className="text-white text-xs">V1 Tips</label>
                        </div>
                        <Switch
                          id="v1-tips-toggle"
                          data-testid="v1-tips-toggle"
                          checked={showV1TipsInChat}
                          onCheckedChange={setShowV1TipsInChat}
                          className="data-[state=checked]:bg-purple-500 data-[state=unchecked]:bg-white/20 h-4 w-7"
                        />
                      </div>
                      <p className="text-white/40 text-[10px] pl-6">
                        Enable V1 Coaching Tips
                      </p>
                    </div>
                  </div>
                  
                  {/* Reply Length */}
                  <div className="space-y-2">
                    <label className="text-white/60 text-xs">Response Length</label>
                    <select
                      value={maxWords}
                      onChange={(e) => setMaxWords(parseInt(e.target.value))}
                      className="w-full bg-divine-black/80 border border-white/20 text-white text-sm rounded px-3 py-2 focus:ring-divine-gold/50 focus:border-divine-gold/50 cursor-pointer"
                    >
                      <option value={75}>Short (~75 words)</option>
                      <option value={150}>Medium (~150 words)</option>
                      <option value={300}>Long (~300 words)</option>
                    </select>
                  </div>

                  {/* Model Selection */}
                  <div className="space-y-2">
                    <label className="text-white/60 text-xs">AI Model</label>
                    <select
                      value={selectedModel ? `${selectedModel.provider}/${selectedModel.name}` : "null/null"}
                      onChange={(e) => {
                        if (e.target.value === "null/null") {
                          setSelectedModel(null);
                        } else {
                          const [provider, name] = e.target.value.split("/");
                          const model = availableModels.find(m => m.provider === provider && m.name === name);
                          if (model) setSelectedModel(model);
                        }
                      }}
                      className="w-full bg-divine-black/80 border border-white/20 text-white text-sm rounded px-3 py-2 focus:ring-divine-gold/50 focus:border-divine-gold/50 cursor-pointer"
                      data-testid="model-selector"
                    >
                      {availableModels.map((model) => (
                        <option key={model.provider ? `${model.provider}/${model.name}` : "null/null"} value={model.provider ? `${model.provider}/${model.name}` : "null/null"}>
                          {model.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Token/Cost Dropdown - Desktop only, hidden on mobile */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setTokenDropdownOpen(!tokenDropdownOpen)}
                className="flex items-center gap-2 px-2 py-1 bg-gradient-to-r from-divine-gold/10 to-purple-500/10 border border-divine-gold/30 rounded-lg hover:from-divine-gold/20 hover:to-purple-500/20 transition-all text-xs"
              >
                <div className="flex items-center gap-1">
                  <Zap className="w-3 h-3 text-divine-gold" />
                  <span className="text-white font-medium">{totalTokens.toLocaleString()}</span>
                </div>
                <div className="w-px h-3 bg-white/20" />
                <div className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3 text-divine-gold" />
                  <span className="text-divine-gold font-medium">{formatCost(totalCost)}</span>
                </div>
                <ChevronDown className={`w-3 h-3 text-white/50 transition-transform ${tokenDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Content */}
              {tokenDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setTokenDropdownOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-52 bg-black/95 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="p-3 space-y-2">
                      {/* Chat tokens */}
                      <div className="flex items-center justify-between">
                        <span className="text-white/50 text-xs">Chat Tokens</span>
                        <span className="text-white font-medium text-sm">{(sessionTokens || 0).toLocaleString()}</span>
                      </div>
                      {/* Coaching tokens - only show if > 0 */}
                      {coachingTokens > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-cyan-400/70 text-xs">Coaching Tokens</span>
                          <span className="text-cyan-400 font-medium text-sm">{coachingTokens.toLocaleString()}</span>
                        </div>
                      )}
                      {/* Divider if coaching tokens exist */}
                      {coachingTokens > 0 && (
                        <div className="border-t border-white/10 my-1" />
                      )}
                      {/* Combined total */}
                      <div className="flex items-center justify-between">
                        <span className="text-white/50 text-xs">{coachingTokens > 0 ? "Total Tokens" : "Total Tokens"}</span>
                        <span className="text-white font-medium text-sm">{totalTokens.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white/50 text-xs">Est. Cost</span>
                        <span className="text-divine-gold font-medium text-sm">{formatCost(totalCost)}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default ChatHeader;
